<template>
  <Head
    :title="
      t('page.title', {
        institutionName: institution.name,
      })
    "
  ></Head>
  <s-page-block class="flex flex-col gap-5">
    <s-page-header
      :heading="
        t('page.title', {
          institutionName: institution.name,
        })
      "
    ></s-page-header>

    <div class="flex flex-col">
      <div class="flex gap-2 flex-wrap items-end justify-between mb-4">
        <div class="lg:flex-1 flex items-center gap-2">
          <h2 class="text-2xl leading-tight text-pretty flex flex-row">
            {{ t('page.registrations.title') }}
          </h2>
          <s-badge color="gray" size="sm">{{ ltiRegistrations.length }}</s-badge>
        </div>
        <div class="flex flex-wrap gap-x-2 gap-y-3">
          <s-dropdown-menu :items="supportedIntegrations">
            <template #trigger="{open}">
              <button class="btn sm primary-light !bg-white flex items-center gap-1.5">
                <s-icon
                  :name="open ? 'close' : 'plus'"
                  size="18"
                  class="opacity-70 transition-all"
                />
                {{ t('actions.add') }}
              </button>
            </template>
          </s-dropdown-menu>
        </div>
      </div>
      <section v-if="ltiRegistrations.length" class="stack vertical gap-3">
        <s-link
          v-for="ltiRegistration in ltiRegistrations"
          :href="
            route('institutions.lti-registrations.lti-deployments.index', {
              institution: institution.id,
              ltiRegistration: ltiRegistration.id,
            })
          "
          class="group card-soft border border-gray-200 transition hover:from-gray-100 hover:to-gray-50 px-4 md:px-5 lg:px-6 py-2.5 md:py-4 stack horizontal align-between valign-center gap-3 text-gray-700 hover:text-blue-500"
        >
          <div class="stack vertical gap-2 w-full">
            <div class="text-lg leading-tight">
              {{ t('page.registrations.card.clientId') }}:
              {{ ltiRegistration.clientId }}
            </div>
          </div>
          <div class="stack horizontal gap-4 w-fit text-nowrap">
            <s-icon
              name="chevron-right"
              class="text-blue-500 opacity-50 group-hover:opacity-80 group-active:opacity-100 transition"
            />
          </div>
        </s-link>
      </section>
      <s-placeholder v-else>
        <p>{{ t('page.registrations.emptyList') }}</p>
      </s-placeholder>
    </div>
  </s-page-block>
</template>
<script setup lang="ts">
import {Head} from '@inertiajs/vue3';
import SPageBlock from '../../design-system/SPageBlock.vue';
import SPageHeader from '../../design-system/SPageHeader.vue';
import {useI18n} from 'vue-i18n';
import {Institution} from '../../types/entities/institution';
import SBtn from '../../design-system/SBtn.vue';
import SPlaceholder from '../../design-system/SPlaceholder.vue';
import SBadge from '../../design-system/SBadge.vue';
import {useFlash} from '../../composables/useFlash';
import {route} from 'ziggy-js';
import SLink from '../../components/SLink.vue';
import SIcon from '../../design-system/SIcon.vue';
import {LaravelDate} from '../../types/laravel-date';
import SDropdownMenu from '../../design-system/SDropdownMenu.vue';
import {computed} from 'vue';

const props = defineProps<{
  institution: Institution;
  ltiRegistrations: {
    id: number;
    platformUrl: string;
    issuer: string;
    clientId: string;
    createdByEmail: string;
    createdAt: LaravelDate;
  }[];
  supportedIntegrations: string[];
}>();

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});

const supportedIntegrations = computed(() => {
  return props.supportedIntegrations.map((lmsName) => ({
    label: lmsName,
    as: 'button',
    onClick: () => copyDynamicRegistrationLinkToClipboard(lmsName),
  }));
});

const flash = useFlash();

const copyDynamicRegistrationLinkToClipboard = (lms: string) => {
  const registrationLink = route('institutions.lti-platform-registrations.create', {
    institution: props.institution.id,
    _query: {
      lms,
    },
  });
  navigator.clipboard.writeText(registrationLink).then(() => {
    flash?.value.set('success', t('page.registrations.copyLinkSuccess'));

    setTimeout(() => {
      flash?.value?.dismiss('success');
    }, 5000);
  });
};
</script>
<i18n>
{
  "en": {
    "page": {
      "title": "LTI Administration - {institutionName}",
      "registrations": {
        "title": "LTI Registrations",
        "emptyList": "No registrations to display",
        "copyLinkSuccess": "Dynamic Registration URL Copied to Clipboard",
        "card": {
          "clientId": "Client ID",
          "createdByEmail": "Created By",
          "createdAt": "Created At"
        }
      }
    }
  },
  "fr": {
    "page": {
      "title": "Administration LTI - {institutionName}",
      "registrations": {
        "title": "Inscriptions LTI",
        "emptyList": "Aucune inscription à afficher",
        "copyLinkSuccess": "URL d'inscription dynamique copiée dans le presse-papiers",
        "card": {
          "clientId": "ID du client",
          "createdByEmail": "Créé par",
          "createdAt": "Créé le"
        }
      }
    }
  }
}
</i18n>
